import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { firstValueFrom, timeout, TimeoutError } from 'rxjs';

import { WINDOW } from '../providers/window.token';
import { environment } from '../../../environments/environment';
import { AuthTokenStore } from './auth-token.store';

/**
 * Same-origin on purpose, and it only became possible with PS-6519.
 *
 * Cross-origin was tried and refused: the CloudFront CSP shared by all 17 Connect brand
 * distributions allows `*.ebuero.de` but no other brand's `webapi` host, so a POST from
 * `connect.thelem.fr` to `https://webapi.thelem.fr/rest/login` never left the browser. PS-6519 put
 * a behavior on this exact path on every distribution, routing it to the brand's login proxy, which
 * makes the call `'self'` and the CSP irrelevant. Locally `proxy.conf.json` maps the same path to
 * the test Lambda — so local and deployed differ in the target and in nothing else.
 */
export const LOGIN_PATH = '/rest/login';

/**
 * The host answered something that is not a login response. Deliberately DESCRIPTIVE, not a
 * diagnosis — we cannot tell the causes apart from here, and an earlier version of this string
 * asserted one ("login path is not routed to the proxy on this host") that turned out to be the
 * wrong one.
 *
 * What actually produces it, measured on live 2026-09-11 and explained by the infrastructure owner
 * (PS-6519): the login proxy answers HTTP 500 whenever it ATTEMPTS an authentication and it fails,
 * and the Connect distribution's CloudFront `custom_error_responses` map 403/404/500 to
 * `200 /index.html` for SPA deep-link routing. So a plain wrong password comes back as 200 with the
 * SPA's HTML, indistinguishable from a genuinely unrouted path. 400s are not in that mapping and
 * pass through as real JSON, which is why a malformed body still produces a proper error.
 *
 * Until the proxy returns 401 for a refused login instead of 500, this path cannot distinguish a
 * wrong password from a server fault, and the customer-facing copy must not pretend otherwise.
 * It is therefore classified as `rejected`, not `failed`: a mistyped password is by far the common
 * cause, and "unavailable" would send every customer who mistyped to the service contact. Both
 * screens offer that contact anyway, so a real server fault is not a dead end either.
 */
const NO_LOGIN_RESPONSE_REASON = 'host did not answer with a login response';

/**
 * How long the sign-in may take before it counts as a technical failure (PS-6495 M4). Without a
 * limit a hung proxy left an unexplained spinner with the step's cancel button disabled.
 */
export const LOGIN_TIMEOUT_MS = 20_000;

/**
 * The proxy relays the password grant's `twofactor_required` under this name. It is NOT a failure:
 * the credentials were right and the customer now has to supply a code.
 */
const TWO_FACTOR_REASON = 'twofactor_code_missing';

/**
 * Both fields feed the app-device lookup in the authorization server's `CheckTokenResponseEnricher`.
 * This app has no per-device identity to offer — no tracking id, nothing persisted — so the value is
 * a constant, which means every Connect login shares one device id. That was accepted with a real
 * login on live (2026-09-09) and nothing downstream objected. What the server does with a shared id
 * over time is UNVERIFIED; if it turns out to matter, this is the one line to change.
 */
const DEVICE_ID = 'connect';
const DEVICE_OS = 'Other';

export type InlineLoginOutcome =
	/** A token is in the store and the caller may proceed. */
	| { result: 'authorized' }
	/** The account needs a second factor; the same credentials are resent with the code. */
	| { result: 'twoFactorRequired' }
	/**
	 * The proxy said no, the server refused the credentials outright (401/403), or the host answered
	 * a 2xx that is not a login response — on the Connect hosts a refused login arrives that way
	 * (see `NO_LOGIN_RESPONSE_REASON`). It does NOT say why — see `classify()`. Shown as
	 * `sip_login_rejected`; `reason` is for the console only.
	 */
	| { result: 'rejected'; reason?: string }
	/**
	 * A TECHNICAL failure — we never got a verdict: no response (network, timeout), a 5xx or other
	 * status without the proxy's body, or a body that is not a login response. Shown as
	 * `sip_login_unavailable`.
	 */
	| { result: 'failed'; reason: string };

export interface InlineLoginCredentials {
	username: string;
	password: string;
	twoFactorCode?: string;
}

interface LoginResponse {
	access_token?: string;
	/**
	 * Seconds, and NOT the client's nominal lifetime: the authorization server reuses a still-valid
	 * token for the same client and user, so this is whatever is left of one that may already be
	 * hours old. Kept so the flow can tell a stale token from a fresh one instead of discovering it
	 * through a 401 in the middle of a booking.
	 */
	expires_in?: number;
}

interface LoginErrorBody {
	result?: string;
	reason?: string;
	message?: string;
}

/**
 * The login, inside the dialog. Replaces the popup and its `authorization_code` + PKCE machinery.
 *
 * What this service deliberately does NOT keep: the `refresh_token` the proxy returns. The token
 * store has no place for one (D3, memory only) and this flow lasts minutes, so a renewable
 * credential in a public page would be scope we do not need.
 *
 * One measured property worth knowing when reading the failure paths: the authorization server
 * REUSES a still-valid token for the same client and user (`findExistingValidToken`), so a second
 * sign-in inside the token's lifetime returns the same value with its remaining lifetime, not a
 * fresh 12 hours. The token can therefore arrive close to expiry, and a later call answering 401 is
 * a normal outcome rather than a defect.
 */
@Injectable({ providedIn: 'root' })
export class InlineLoginService {
	private readonly window: Window = inject(WINDOW);
	private readonly store: AuthTokenStore = inject(AuthTokenStore);
	private readonly httpClient: HttpClient = inject(HttpClient);

	async signIn(credentials: InlineLoginCredentials): Promise<InlineLoginOutcome> {
		const body: Record<string, string> = {
			username: credentials.username,
			password: credentials.password,
			domain: this.loginDomain,
			grant_type: 'password',
			device_id: DEVICE_ID,
			device_os: DEVICE_OS,
			app_name: `connect.${this.brandRoot()}`,
		};
		// Sent only when there is one. The grant answers `twofactor_required` for a blank value just
		// as it does for a missing one, so an empty string would turn the first attempt into a
		// pointless round trip.
		if (credentials.twoFactorCode) {
			body['twofactorcode'] = credentials.twoFactorCode;
		}

		try {
			const response = await firstValueFrom(
				this.httpClient.post<LoginResponse>(LOGIN_PATH, body).pipe(timeout(LOGIN_TIMEOUT_MS)),
			);
			// Two different shapes of "this host did not answer with a login response", and the one
			// that hits depends on the body: HttpXhrBackend raises a 2xx ERROR when JSON.parse throws
			// (see classify), but hands a non-JSON body straight through when it parses or when the
			// backend is stubbed. Both mean the same thing on a deployed host — `/rest/login` is not
			// routed to the proxy and CloudFront served the SPA — so both have to say so.
			if (response === null || typeof response !== 'object') {
				return { result: 'rejected', reason: `${NO_LOGIN_RESPONSE_REASON} (2xx, non-JSON body)` };
			}
			if (!response.access_token) {
				return { result: 'failed', reason: 'login response carried no access_token' };
			}
			this.store.setToken(response.access_token, response.expires_in);
			return { result: 'authorized' };
		} catch (error: unknown) {
			return this.classify(error);
		}
	}

	/**
	 * Distinguishes "the proxy said no" from "we never reached the proxy", which look identical from
	 * the status code alone. The proxy always answers a JSON body carrying `reason`; a response
	 * without one did not come from it — a missing dev-proxy entry produces a local 404 that would
	 * otherwise read exactly like refused credentials, and that mistake already cost an hour on the
	 * popup flow. So the status travels in the reason string, for the log and never for the customer.
	 */
	private classify(error: unknown): InlineLoginOutcome {
		if (error instanceof TimeoutError) {
			return { result: 'failed', reason: `login timed out after ${LOGIN_TIMEOUT_MS} ms` };
		}
		const status = error instanceof HttpErrorResponse ? error.status : 0;
		const body: LoginErrorBody | null =
			error instanceof HttpErrorResponse && error.error && typeof error.error === 'object'
				? (error.error as LoginErrorBody)
				: null;

		// A 2xx that still lands in the error path means the transport SUCCEEDED and the body could
		// not be parsed — Angular only errors on a 2xx when JSON.parse throws. On a deployed Connect
		// host that has two causes and we cannot tell them apart: the proxy answered an error status
		// that CloudFront laundered into the SPA (the common one — a refused login is a 500), or the
		// path is genuinely not routed (which is the state of the test distributions).
		//
		// It gets its own reason because the generic one reads as a verdict on the customer's
		// credentials ("login rejected (HTTP 200)") when nothing rejected anything. The customer sees
		// `sip_login_rejected` (see `NO_LOGIN_RESPONSE_REASON` for why); this text is for the console.
		if (status >= 200 && status < 300) {
			return {
				result: 'rejected',
				reason: `${NO_LOGIN_RESPONSE_REASON} (HTTP ${status}, unparsable body)`,
			};
		}

		if (body?.reason === TWO_FACTOR_REASON) {
			return { result: 'twoFactorRequired' };
		}
		if (body?.reason) {
			// `rejected`, NOT `invalidCredentials`. The proxy's only non-2FA reason is `generic`
			// (portal-login/src/js/helpers/login.ts:67-71 types the union), and it carries a refused
			// password and a server-side failure under the same value — the branch's own mock serves
			// it with HTTP 500. So the response does not say the credentials were wrong, and neither
			// may we. The proxy's own text stays unsurfaced: it is generic ("Es ist ein Fehler
			// aufgetreten") and would not help.
			return { result: 'rejected' };
		}
		/**
		 * A bare 401/403 is a refusal of the credentials even without the proxy's body. Every other
		 * status without it - 0 (network), 404 (unrouted), 5xx - is technical.
		 */
		if (status === 401 || status === 403) {
			return { result: 'rejected' };
		}
		return { result: 'failed', reason: `login rejected (HTTP ${status})` };
	}

	/**
	 * Exactly what `signIn()` puts in the `domain` field, exposed so the login probe can SHOW it.
	 * That is the whole question on a non-ebuero brand: a value that does not match `MANDANT_DOMAIN`
	 * refuses every customer on the host with `invalid_domain`, and the refusal looks identical to
	 * a wrong password.
	 */
	get loginDomain(): string {
		return `https://${this.brandRoot()}`;
	}

	/**
	 * The brand root exactly as `MANDANT_DOMAIN` stores it, because that table is what the password
	 * grant looks the domain up in. `cleanUpDomainFromPrefixes` strips only `www.` and the scheme, so
	 * `connect.` and `test.` have to come off here — sending the page's own hostname would be looked
	 * up verbatim and refused with `invalid_domain` for every customer.
	 *
	 * A dev hostname yields no brand at all, so the configured fallback stands in.
	 */
	private brandRoot(): string {
		const host = this.window.location.hostname
			.replace(/^connect\./, '')
			.replace(/^test\./, '');
		return host.includes('.') ? host : (environment.FALLBACK_DOMAIN ?? host);
	}
}
